import { useState, useEffect, useMemo, useRef } from 'react';
import { api, CATEGORIES, getCategoryColor } from '@/lib/api';
import GigCard from '@/components/GigCard';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Search, MapIcon, List, SlidersHorizontal, X, Navigation, MapPin } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const RADIUS_MIN = 1;
const RADIUS_MAX = 100;

export default function Explore() {
  const [opportunities, setOpportunities] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [view, setView] = useState('list');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [locating, setLocating] = useState(false);
  const [radius, setRadius] = useState(50);
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const autoLocated = useRef(false);

  // Auto-detect location on mount
  useEffect(() => {
    if (!autoLocated.current && navigator.geolocation) {
      autoLocated.current = true;
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude);
          setUserLng(pos.coords.longitude);
          setLocating(false);
          toast.success('Location detected! Gigs sorted by distance.');
        },
        (err) => {
          setLocating(false);
          // Silently fall back - don't block the page
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, []);

  useEffect(() => {
    loadOpportunities();
  }, [category, type, userLat, userLng, zipCode, radius, radiusEnabled]);

  const getUserLocation = () => {
    if (userLat && userLng) {
      // Already have location, just toggle it off/on
      setUserLat(null);
      setUserLng(null);
      setRadiusEnabled(false);
      toast.info('Location filter removed');
      return;
    }
    setLocating(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setLocating(false);
        toast.success('Location detected! Gigs sorted by nearest.');
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) {
          toast.error('Location access denied. Please allow location in your browser settings.');
        } else if (err.code === 2) {
          toast.error('Location unavailable. Try again later.');
        } else {
          toast.error('Location request timed out. Try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const loadOpportunities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: 'open' });
      if (category) params.append('category', category);
      if (type) params.append('type', type);
      if (zipCode) params.append('zip_code', zipCode);
      if (userLat !== null && userLng !== null) {
        params.append('lat', userLat);
        params.append('lng', userLng);
        if (radiusEnabled) params.append('radius', radius);
      }
      const res = await api.get(`/opportunities?${params}`);
      setOpportunities(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!search) return opportunities;
    const q = search.toLowerCase();
    return opportunities.filter(o =>
      o.title.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q)
    );
  }, [opportunities, search]);

  const mapCenter = useMemo(() => {
    if (userLat && userLng) return [userLat, userLng];
    const withCoords = filtered.filter(o => o.location?.lat && o.location?.lng);
    if (withCoords.length === 0) return [40.7128, -74.006];
    const avgLat = withCoords.reduce((s, o) => s + o.location.lat, 0) / withCoords.length;
    const avgLng = withCoords.reduce((s, o) => s + o.location.lng, 0) / withCoords.length;
    return [avgLat, avgLng];
  }, [filtered, userLat, userLng]);

  const handleZipSearch = (e) => {
    if (e.key === 'Enter') {
      loadOpportunities();
    }
  };

  const sliderPercent = ((radius - RADIUS_MIN) / (RADIUS_MAX - RADIUS_MIN)) * 100;

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto" data-testid="explore-page">
      <SEO
        title="Explore Opportunities"
        description="Browse volunteer opportunities and paid side gigs near you. Filter by category, distance, and type to find the perfect match."
        path="/explore"
      />
      <h1 className="text-3xl font-bold text-[#011627] mb-4 animate-fade-in-up">Explore</h1>

      {/* Search Bar */}
      <div className="relative mb-3 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA5B1]" />
        <Input
          placeholder="Search gigs..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="pl-11 bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
          data-testid="search-input"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Location Controls */}
      <div className="flex gap-2 mb-3 animate-fade-in-up" style={{ animationDelay: '120ms' }}>
        <button
          onClick={getUserLocation}
          disabled={locating}
          className={`flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-sm font-bold border-2 transition-all ${
            userLat ? 'border-[#2EC4B6] bg-[#2EC4B6]/10 text-[#2EC4B6]' : 'border-gray-200 text-[#5C6B7F]'
          }`}
          data-testid="use-location-btn"
        >
          <Navigation size={14} className={locating ? 'animate-spin' : ''} />
          {locating ? 'Locating...' : userLat ? 'Near Me' : 'Use My Location'}
        </button>
        <div className="relative flex-1">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA5B1]" />
          <Input
            placeholder="Zip Code"
            value={zipCode}
            onChange={e => setZipCode(e.target.value)}
            onKeyDown={handleZipSearch}
            className="pl-9 bg-white border-2 border-gray-100 focus:border-[#FF9F1C] rounded-2xl py-2.5 text-sm font-semibold"
            data-testid="zip-code-input"
          />
          {zipCode && (
            <button onClick={() => { setZipCode(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Radius Slider Filter */}
      {userLat && (
        <div className="bg-white rounded-2xl p-4 border-2 border-gray-100 mb-4 animate-fade-in-up" style={{ animationDelay: '140ms' }}>
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="radius-toggle">
              <input
                type="checkbox"
                checked={radiusEnabled}
                onChange={(e) => setRadiusEnabled(e.target.checked)}
                className="w-4 h-4 accent-[#2EC4B6] rounded"
              />
              <span className="text-sm font-bold text-[#011627]">Filter by distance</span>
            </label>
            {radiusEnabled && (
              <span className="text-sm font-bold text-[#2EC4B6]" data-testid="radius-value">{radius} mi</span>
            )}
          </div>
          {radiusEnabled && (
            <div className="relative pt-1">
              <input
                type="range"
                min={RADIUS_MIN}
                max={RADIUS_MAX}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #2EC4B6 0%, #2EC4B6 ${sliderPercent}%, #E5E7EB ${sliderPercent}%, #E5E7EB 100%)`,
                }}
                data-testid="radius-slider"
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] font-semibold text-[#9AA5B1]">{RADIUS_MIN} mi</span>
                <span className="text-[10px] font-semibold text-[#9AA5B1]">{RADIUS_MAX} mi</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Toggle + Filter Button */}
      <div className="flex items-center gap-2 mb-4 animate-fade-in-up" style={{ animationDelay: '160ms' }}>
        <div className="flex bg-gray-100 rounded-full p-1 flex-shrink-0">
          <button
            onClick={() => setView('list')}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${view === 'list' ? 'bg-white text-[#011627] shadow-sm' : 'text-[#5C6B7F]'}`}
            data-testid="view-list"
          >
            <List size={16} />
          </button>
          <button
            onClick={() => setView('map')}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${view === 'map' ? 'bg-white text-[#011627] shadow-sm' : 'text-[#5C6B7F]'}`}
            data-testid="view-map"
          >
            <MapIcon size={16} />
          </button>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all flex items-center gap-1 ${
            showFilters || category || type ? 'border-[#2EC4B6] text-[#2EC4B6] bg-[#2EC4B6]/5' : 'border-gray-200 text-[#5C6B7F]'
          }`}
          data-testid="filter-toggle"
        >
          <SlidersHorizontal size={14} /> Filters
          {(category || type) && <span className="w-2 h-2 bg-[#2EC4B6] rounded-full" />}
        </button>

        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {['', 'volunteer', 'paid'].map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border-2 ${
                type === t ? 'border-[#FF9F1C] bg-[#FF9F1C]/10 text-[#FF9F1C]' : 'border-gray-100 text-[#5C6B7F]'
              }`}
              data-testid={`type-${t || 'all'}`}
            >
              {t === '' ? 'All' : t === 'volunteer' ? 'Volunteer' : 'Paid'}
            </button>
          ))}
        </div>
      </div>

      {/* Category Filters */}
      {showFilters && (
        <div className="mb-4 animate-fade-in">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory('')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                !category ? 'border-[#2EC4B6] bg-[#2EC4B6] text-white' : 'border-gray-100 text-[#5C6B7F]'
              }`}
              data-testid="cat-all"
            >
              All
            </button>
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setCategory(category === c.value ? '' : c.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                  category === c.value ? 'text-white' : 'border-gray-100 text-[#5C6B7F]'
                }`}
                style={category === c.value ? { borderColor: c.color, backgroundColor: c.color } : {}}
                data-testid={`cat-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results Count */}
      <p className="text-sm font-bold text-[#9AA5B1] mb-3" data-testid="results-count">
        {filtered.length} opportunities found
        {userLat && ' · sorted by nearest'}
        {radiusEnabled && ` · within ${radius} mi`}
        {zipCode && ` · zip: ${zipCode}`}
      </p>

      {/* Map View */}
      {view === 'map' && (
        <div className="mb-4 rounded-3xl overflow-hidden border-2 border-gray-100" style={{ height: '300px' }} data-testid="map-view">
          <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.filter(o => o.location?.lat && o.location?.lng).map(o => (
              <Marker key={o.opp_id} position={[o.location.lat, o.location.lng]}>
                <Popup>
                  <div className="font-semibold text-sm">{o.title}</div>
                  <div className="text-xs text-gray-500">{o.location.name}</div>
                  {o.distance !== undefined && <div className="text-xs font-bold text-blue-600">{o.distance} mi away</div>}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* List View */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-3xl p-5 border-2 border-gray-100 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
              <div className="h-5 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-8 border-2 border-gray-100 text-center">
          <Search size={40} className="text-[#9AA5B1] mx-auto mb-3" />
          <p className="text-[#5C6B7F] font-semibold">No opportunities found</p>
          <p className="text-sm text-[#9AA5B1]">Try adjusting your filters or increasing the radius</p>
        </div>
      ) : (
        <div className="space-y-3 stagger-children">
          {filtered.map(opp => (
            <div key={opp.opp_id} className="animate-fade-in-up">
              <GigCard opportunity={opp} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
